import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { ReadTransaction } from "@workcanvas/reflect";
import consts from "shared/consts";
import { FileMetadataType } from "shared/datamodel/schemas";
import { canvasElementPrefix, canvasMetadataPrefix } from "shared/util/utils";
import { isOfTypes } from "./canvas-designer-new/elements/canvas-elements-utils";

// small functions to check the type of an element by its key
const isFileType = isOfTypes(consts.CANVAS_ELEMENTS.FILE);
const isOrgChartType = isOfTypes(consts.CANVAS_ELEMENTS.ORG_CHART);
const isAnIntegratedElement = isOfTypes(
  consts.CANVAS_ELEMENTS.INTEGRATION,
  consts.CANVAS_ELEMENTS.LIVE_INTEGRATION,
  consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART
);

/**
 * Get the data needed to create a template from the canvas
 * @param syncService
 * @param filterOutIntegrations - if true, remove all elements that have integrations
 * @returns {[key: string]: any}
 */
export async function getCanvasDataForTemplate(syncService: SyncService<RW> | null, filterOutIntegrations: boolean) {
  const thumbnailId = await syncService?.mutate.getThumbnailId();
  return syncService?.getReplicache()?.query(async (tx: ReadTransaction) => {
    const elements: Record<string, any> = {};
    const existingImages = new Set<string>();

    for await (const [key, value] of tx.scan({ prefix: canvasElementPrefix }).entries()) {
      const element = value as any;
      // ignore hidden elements
      if (element.hidden) continue;
      // ignore elements in the thumbnail frame
      if (thumbnailId) {
        if (key.endsWith(thumbnailId) || ("frameId" in element && element.frameId == thumbnailId)) {
          continue;
        }
      }
      if (filterOutIntegrations && (element.integrationId || isAnIntegratedElement(key))) continue;
      // remember image elements that show up in the template
      if (isFileType(key)) {
        existingImages.add(canvasMetadataPrefix + FileMetadataType + "-" + element.fileId);
      }
      // check orgchart photos also
      if (isOrgChartType(key)) {
        for (const nodeId in element.data) {
          if (element.data[nodeId].photo) {
            existingImages.add(canvasMetadataPrefix + FileMetadataType + "-" + element.data[nodeId].photo);
          }
        }
      }

      // otherwise, insert element to the result
      elements[key] = value;
    }

    // Clean the elements object from stray connectors (connectors that are attached
    // to hidden (aka deleted) elements). Those connectors were copied also
    // but shouldn't exist since they're not show on the canvas
    for (const [key, value] of Object.entries(elements)) {
      const element = value as any;
      if (element.type == "connector") {
        let includeConnector = true;
        for (const connection of element.connectedShapes) {
          if (connection != null && !!connection.id)
            if (!elements[canvasElementPrefix + connection.id]) {
              includeConnector = false;
              break;
            }
        }
        if (!includeConnector) {
          delete elements[key];
        }
      }
    }

    // function to check if a key is a file metadata entry
    const isAFileMetadataEntry = (key: string) => key.startsWith(canvasMetadataPrefix + FileMetadataType);

    // add metadata entries (filter out files that are not referenced in the elements)
    let metadataEntries = await tx.scan({ prefix: canvasMetadataPrefix }).entries().toArray();
    metadataEntries = metadataEntries.filter(([key, _]) => !isAFileMetadataEntry(key) || existingImages.has(key));
    const metadata = Object.fromEntries(metadataEntries);

    Object.assign(elements, metadata);
    return elements;
  });
}
